/**
 * Elegir una plantilla aprobada, rellenarla y enviarla.  (spec 056)
 */
import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import { useSoloLectura } from '../auth'
import { useEsAdminUi } from '../ver-como'
import { api, ErrorApi } from '../lib/api'
import { rellenarHuecos, rotuloHueco, sePuedeEnviar, type CitaPlantilla } from '../lib/plantilla-huecos'

interface Plantilla {
    nombre: string
    idioma: string
    categoria: string
    cuerpo: string
    huecos: string[]
    estado: string
}

interface Lista {
    contactId: number
    plantillas: Plantilla[]
    cita: CitaPlantilla | null
}

export function BotonEnviarPlantilla({
    accountId,
    contactId,
    conversationId,
}: {
    accountId: number
    contactId: number | null
    conversationId: number | null
}) {
    const [abierto, setAbierto] = useState(false)
    const esAdmin = useEsAdminUi()
    const soloLectura = useSoloLectura()
    if (!esAdmin || soloLectura) return null
    return (
        <>
            <button
                type="button"
                onClick={() => setAbierto(true)}
                className="text-sky-400 hover:text-sky-300"
            >
                Enviar plantilla
            </button>
            {abierto && (
                <DialogoPlantilla
                    accountId={accountId}
                    contactId={contactId}
                    conversationId={conversationId}
                    onCerrar={() => setAbierto(false)}
                />
            )}
        </>
    )
}

function marcaEstado(estado: string): string | null {
    if (estado === 'APPROVED') return null
    if (estado === 'PENDING') return 'en revisión'
    if (estado === 'REJECTED') return 'rechazada'
    if (estado === 'PAUSED') return 'en pausa'
    return estado ? estado.toLowerCase() : 'sin estado'
}

function DialogoPlantilla({
    accountId,
    contactId,
    conversationId,
    onCerrar,
}: {
    accountId: number
    contactId: number | null
    conversationId: number | null
    onCerrar: () => void
}) {
    const [refresco, setRefresco] = useState(0)
    const params = new URLSearchParams()
    if (contactId !== null) params.set('contactId', String(contactId))
    if (conversationId !== null) params.set('conversationId', String(conversationId))
    const consulta = params.toString()

    const { data, isPending, isError, error, isFetching } = useQuery({
        queryKey: ['plantillas', accountId, consulta, refresco],
        queryFn: () => api.get<Lista>(`/business/${accountId}/plantillas?${consulta}`),
        enabled: consulta.length > 0,
    })

    const [elegida, setElegida] = useState<string | null>(null)
    const [valores, setValores] = useState<Record<string, string>>({})
    const [enviando, setEnviando] = useState(false)
    const [aviso, setAviso] = useState<string | null>(null)
    const [hecho, setHecho] = useState<string | null>(null)

    const plantilla = data?.plantillas.find((p) => `${p.nombre}::${p.idioma}` === elegida) ?? null

    useEffect(() => {
        if (!plantilla || !data) return
        setValores(rellenarHuecos(plantilla.huecos, data.cita))
        setAviso(null)
        setHecho(null)
    }, [plantilla, data])

    useEffect(() => {
        function tecla(e: KeyboardEvent) {
            if (e.key === 'Escape') onCerrar()
        }
        window.addEventListener('keydown', tecla)
        return () => window.removeEventListener('keydown', tecla)
    }, [onCerrar])

    const enviar = async () => {
        if (!plantilla || !data || plantilla.estado !== 'APPROVED') return
        setEnviando(true)
        setAviso(null)
        try {
            const r = await api.post<{
                ok: true
                conversationId: number
                pendiente: boolean
                via: 'plantilla' | 'sesion'
            }>(`/business/${accountId}/plantillas`, {
                contactId: data.contactId,
                conversationId,
                nombre: plantilla.nombre,
                idioma: plantilla.idioma,
                categoria: plantilla.categoria,
                cuerpo: plantilla.cuerpo,
                valores,
            })
            setHecho(
                r.via === 'sesion'
                    ? 'La ventana de 24 h está abierta. Se envió un mensaje normal, sin plantilla.'
                    : r.pendiente
                      ? 'Enviada. El hilo queda en espera: cuando contesten, el asistente sigue.'
                      : 'El mensaje salió. No se pudo dejar el hilo en espera.',
            )
        } catch (e) {
            setAviso(e instanceof ErrorApi ? e.message : 'No se pudo enviar')
        } finally {
            setEnviando(false)
        }
    }

    return (
        <div
            className="fixed inset-0 z-[60] flex items-end justify-center bg-black/70 p-4 sm:items-center"
            role="dialog"
            aria-modal="true"
            aria-labelledby="titulo-plantilla"
            onClick={onCerrar}
        >
            <div
                className="flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-lg border border-neutral-800 bg-neutral-950 shadow-xl"
                onClick={(e) => e.stopPropagation()}
            >
                <header className="flex items-center justify-between gap-3 border-b border-neutral-800 px-5 py-4">
                    <h3 id="titulo-plantilla" className="text-base font-semibold text-neutral-100">
                        Enviar plantilla
                    </h3>
                    <div className="flex items-center gap-3 text-sm">
                        <button
                            type="button"
                            onClick={() => setRefresco((n) => n + 1)}
                            className="text-sky-400 hover:text-sky-300"
                            disabled={isFetching}
                        >
                            {isFetching ? 'Actualizando…' : 'Actualizar'}
                        </button>
                        <button type="button" onClick={onCerrar} className="text-neutral-400 hover:text-neutral-200">
                            Cerrar
                        </button>
                    </div>
                </header>
                <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
                    {consulta.length === 0 && (
                        <p className="text-sm text-rose-400">No se ha podido identificar al cliente.</p>
                    )}
                    {isPending && consulta.length > 0 && (
                        <p className="text-sm text-neutral-500">Cargando plantillas…</p>
                    )}
                    {isError && (
                        <p className="text-sm text-rose-400">
                            {error instanceof ErrorApi ? error.message : 'No se pudieron leer las plantillas.'}
                        </p>
                    )}
                    {data && data.plantillas.length === 0 && (
                        <p className="text-sm text-neutral-400">
                            No hay plantillas en este buzón. Si acabas de crearla, pulsa Actualizar.
                        </p>
                    )}
                    {data && data.plantillas.length > 0 && (
                        <ul className="space-y-1">
                            {data.plantillas.map((p) => {
                                const clave = `${p.nombre}::${p.idioma}`
                                const marca = marcaEstado(p.estado)
                                return (
                                    <li key={clave}>
                                        <button
                                            type="button"
                                            onClick={() => setElegida(clave)}
                                            className={
                                                clave === elegida
                                                    ? 'flex w-full items-center justify-between gap-3 rounded border border-sky-700 bg-sky-950/40 px-3 py-2 text-left text-sm text-sky-200'
                                                    : 'flex w-full items-center justify-between gap-3 rounded border border-neutral-800 px-3 py-2 text-left text-sm text-neutral-200 hover:border-neutral-600'
                                            }
                                        >
                                            <span>{p.nombre}</span>
                                            {marca && <span className="shrink-0 text-xs text-amber-300">{marca}</span>}
                                        </button>
                                    </li>
                                )
                            })}
                        </ul>
                    )}
                    {plantilla && (
                        <form
                            className="space-y-3"
                            onSubmit={(e) => {
                                e.preventDefault()
                                void enviar()
                            }}
                        >
                            <p className="whitespace-pre-wrap text-sm text-neutral-400">{plantilla.cuerpo}</p>
                            {data?.cita === null && plantilla.huecos.length > 0 && (
                                <p className="text-xs text-neutral-500">Sin cita próxima. Rellena los huecos a mano.</p>
                            )}
                            {plantilla.huecos.map((h) => (
                                <label key={h} className="block text-sm">
                                    <span className="mb-1 block text-neutral-400">{rotuloHueco(h)}</span>
                                    <input
                                        value={valores[h] ?? ''}
                                        onChange={(e) => setValores((v) => ({ ...v, [h]: e.target.value }))}
                                        className="w-full rounded border border-neutral-700 bg-neutral-900 px-3 py-2 text-neutral-100"
                                    />
                                </label>
                            ))}
                            {plantilla.estado !== 'APPROVED' && (
                                <p className="text-sm text-amber-300">
                                    Meta todavía no la ha aprobado. Se rellena para verla; no se puede enviar.
                                </p>
                            )}
                            {aviso && <p className="text-sm text-rose-400">{aviso}</p>}
                            {hecho && <p className="text-sm text-emerald-400">{hecho}</p>}
                            <button
                                type="submit"
                                disabled={
                                    plantilla.estado !== 'APPROVED' ||
                                    !sePuedeEnviar(plantilla.huecos, valores) ||
                                    enviando ||
                                    hecho !== null
                                }
                                className="rounded bg-sky-700 px-3 py-2 text-sm text-white disabled:cursor-not-allowed disabled:bg-neutral-800 disabled:text-neutral-500"
                            >
                                {enviando ? 'Enviando…' : 'Enviar'}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    )
}
